import axios from "axios";
import { managementApiBaseUrl } from "@/service/totemDeviceConfig";

const CACHE_KEY = "photonow:totem-products:v1";
const CACHE_TTL_MS = 10 * 60 * 1000;

let memorySnapshot = null;
let inFlight = null;

function storageAvailable() {
  return typeof localStorage !== "undefined";
}

function normalizeProduct(product) {
  if (!product || typeof product !== "object") return null;
  const id = String(product.id || "").trim();
  const slug = String(product.slug || "").trim();
  const name = String(product.name || "").trim();
  const priceFromCents = Number(product.priceFromCents);
  if (!id || !name || !Number.isFinite(priceFromCents)) return null;

  return {
    id,
    slug,
    name,
    imageUrl:
      typeof product.imageUrl === "string" && product.imageUrl.trim()
        ? product.imageUrl.trim()
        : null,
    priceFromCents: Math.max(0, Math.round(priceFromCents)),
  };
}

function normalizePayload(payload) {
  if (!payload || !Array.isArray(payload.items)) {
    throw new Error("Catálogo do Totem respondeu em formato inválido.");
  }
  return payload.items.map(normalizeProduct).filter(Boolean).slice(0, 3);
}

function readStoredSnapshot() {
  if (!storageAvailable()) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (!parsed || !Array.isArray(parsed.items)) return null;
    const items = parsed.items
      .map(normalizeProduct)
      .filter(Boolean)
      .slice(0, 3);
    const loadedAt = Number(parsed.loadedAt);
    if (!Number.isFinite(loadedAt)) return null;
    return { items, loadedAt };
  } catch (_error) {
    return null;
  }
}

function currentSnapshot() {
  if (memorySnapshot) return memorySnapshot;
  memorySnapshot = readStoredSnapshot();
  return memorySnapshot;
}

function persistSnapshot(items) {
  memorySnapshot = { items, loadedAt: Date.now() };
  if (!storageAvailable()) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(memorySnapshot));
  } catch (_error) {
    // Storage cheio ou indisponível não impede a vitrine em memória.
  }
}

function fresh(snapshot) {
  return snapshot && Date.now() - snapshot.loadedAt < CACHE_TTL_MS;
}

export function storefrontBaseUrl(hostname = window.location.hostname) {
  if (
    hostname === "cabine-teste.web.app" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"
  ) {
    return "https://teste.photonowoficial.com.br";
  }
  return "https://photonow.com.br";
}

export function productDeepLink(product, hostname = window.location.hostname) {
  const identifier = String(product?.slug || product?.id || "").trim();
  if (!identifier) return "";
  return `${storefrontBaseUrl(hostname)}/produtos/${encodeURIComponent(
    identifier
  )}`;
}

export function getCachedTotemProducts() {
  return currentSnapshot()?.items || [];
}

export async function fetchTotemProducts(options = {}) {
  const cached = currentSnapshot();
  if (!options.force && fresh(cached)) return cached.items;
  if (inFlight) return inFlight;

  inFlight = axios
    .create({
      baseURL: managementApiBaseUrl(),
      timeout: 8000,
      headers: { Accept: "application/json" },
    })
    .get("/catalog/totem-products")
    .then((response) => {
      const items = normalizePayload(response.data);
      persistSnapshot(items);
      return items;
    })
    .catch(() => cached?.items || [])
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function prefetchTotemProducts() {
  return fetchTotemProducts({ force: true });
}

export function resetTotemProductsCache() {
  memorySnapshot = null;
  inFlight = null;
  if (!storageAvailable()) return;
  localStorage.removeItem(CACHE_KEY);
}
